import {Dbg} from '../util.js';
const _ = require('lodash/core');

export class Parser
{
  /** Validates all specified parameters (see parameters.js).
   * @param {Parameter[]} parameters Parameters to be validated.
   * @param {Uint8Array} rawData Raw binary sysex data.
   * @param {boolean} repair If true, fix errors where possible and update binary data.
   * @returns {string[]} Any warning/error messages.
   */
  static validateAll(parameters, rawData, repair = true) {
    let warnings = [];
    parameters.forEach(parameter => {
      let value = parameter.getValue(rawData);
      let warning = Parser.validate(parameter, rawData, value, repair);
      if (warning) warnings.push(result.warning);
    });
    return warnings;
  }

  /** Validates the specified parameter (see parameters.js).
   * @param {Parameter} parameter The specified parameter to validate.
   * @param {Uint8Array} rawData Raw binary sysex data.
   * @param {object} value Specified parameter's value (to check).
   * @param {boolean} repair If true, fix errors where possible and update binary data.
   * @returns {string|undefined} Any warning/error messages. Undefined if all ok.
   */
  static validate(parameter, rawData, value, repair = true) {
    let warning = undefined;
    let result = parameter.validateValue(value);
    if (result.warning) {
      warning = result.warning;
      if (repair) parameter.setValue(rawData, result.value);
    }
  }

  /** Parses all specified parameters (see parameters.js).
   *  (converts all parameters from binary to a structured object)
   * @param {Parameter[]} parameters Parameters to be parsed.
   * @param {Uint8Array} rawData Raw binary sysex data.
   * @param {object} parsedData Object to receive the parsed data.
   * @param {boolean} repair If true, fix errors where possible and update binary data.
   * @returns {string[]} Any warning/error messages.
   */
  static parseAll(parameters, rawData, parsedData, repair = true) {
    let warnings = [];
    if (repair) warnings = Parser.validateAll(parameters, rawData, true);
    parameters.forEach(parameter => {
      parsedData[parameter.name] = parameter.getValue(rawData);
    });
    return warnings;
  }

  /** Parses the specified parameter (see parameters.js).
   *  (converts from binary to a structured object)
   * @param {Parameter} parameter Parameter to parse.
   * @param {string} name Name of the specified parameter.
   * @param {Uint8Array} rawData Raw binary sysex data.
   * @param {boolean} repair If true, fix errors where possible and update binary data.
   * @return {object} Parsing result, containing:
   *  value - the parsed value (type depends on parameter)
   *  warning - warning message (string); undefined if value is valid.
   */
  static parse(parameter, rawData, repair = true) {
    let warning = undefined;
    let value = parameter.getValue(rawData);
    if (repair) warning = Parser.validate(parameter, rawData, value, repair);
    return {value, warning};
  }

  /** Formats all specified parameters (see parameters.js).
   *  (converts all parameters from a structured object to binary)
   * @param {Parameter[]} parameters Parameters to be formatted.
   * @param {Uint8Array} rawData Raw binary sysex data (of the correct size).
   * @param {object} parsedData Object containing the parsed data.
   * @param {boolean} repair If true, fix errors where possible and update binary data.
   * @returns {string[]} Any warning/error messages.
   */
  static formatAll(parameters, rawData, parsedData, repair = true) {
    parameters.forEach(p => {
      p.setValue(rawData, parsedData[p.name]);
    });
    let warnings = [];
    if (repair) warnings = Parser.validateAll(parameters, rawData, true);
    return warnings;
  }



  ///// Static utilities

  /** Checks if the data matches (starts with) a specifications
   *  sequence of bytes (pattern).
   * @param {Uint8Array} data
   * @param {number[]} pattern
   * @returns {boolean} true iff data matches pattern
   */
  static matchesPattern(data, pattern) {
    if (data.length < pattern.length) return false;
    for (let i = 0; i < pattern.length; i++) {
      if (typeof pattern[i] === "undefined") continue; // undefined matches any value
      if (data[i] !== pattern[i]) return false;
    }
    return true;
  }

  /** Computes Yamaha-format checksum
   *  (low 7 bits of 2s complement of byte sum)
   * @param {Uint8Array} data
   */
  static compute2ComplementChecksum(data) {
    let sum = _.reduce(data, function(s,n){return s+n}, 0);
    sum &= 0x7f;
    let checksum = (128 - sum) & 0xf7;
    return checksum;
  }
}



// WEBPACK FOOTER //
// ./src/parsing_formatting/parser.js